import { hexToRawRgb } from "@/helper/helper";
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
} from "@mui/material/styles";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

type OrgThemeProvideType = {
  children: React.ReactNode;
};

type ThemeType = Record<string, string>;

type ThemeContextType = {
  theme: ThemeType | null;
  updateTheme: (newTheme: ThemeType) => void;
  resetTheme: () => void;
};

const defaultTheme = {
  primary: "#4e0da3",
  lightPrimary: "#f3ebff",
  onPrimary: "#ffffff",
  secondary: "#FCAA0B",
  onSecondary: "#ffffff",
  text: "#000000",
  loader: "#4e0da3",
  appBg: "#f8f9fa",
  cardBg: "#ffffff",
};

const ThemeContext = createContext<ThemeContextType>({
  theme: null,
  updateTheme: () => {},
  resetTheme: () => {},
});

export const ThemeProvider = ({ children }: OrgThemeProvideType) => {
  const [theme, setTheme] = useState<ThemeType | null>(null);

  const applyCssVars = (vars: Record<string, string>) => {
    const root = document.documentElement;
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, hexToRawRgb(value));
    });
  };

  const updateTheme = (newTheme: ThemeType) => {
    setTheme(newTheme);
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  const muiTheme = useMemo(() => {
    return createTheme({
      palette: {
        primary: { main: theme?.primary || "#4E0DA3" },
        secondary: {
          main: theme?.secondary || "#FCAA0B",
        },
      },
      typography: {
        fontFamily: `"Poppins", sans-serif`,
      },
    });
  }, [theme]);

  useEffect(() => {
    const org = JSON.parse(localStorage.getItem("org") || "{}");
    if (org?.theme) {
      setTheme(org.theme || {});
    }
  }, []);

  useEffect(() => {
    if (!theme) return;

    applyCssVars({ ...defaultTheme, ...theme });
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, resetTheme }}>
      <MUIThemeProvider theme={muiTheme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeProvider = () => useContext(ThemeContext);
